import classNames from 'classnames';
import { ContractDetailPartyResponse, PartyVerificationStatus } from 'compliance-api/generated';
import { useContext, useMemo, useState } from 'react';
import Badge from '@/modules/common/components/Badge.tsx';
import { CreatedByAiLabel } from '@/modules/common/components/CreatedByAiLabel.tsx';
import ContractPartyModal from '@/modules/admin-dashboard/components/contractManagement/ContractByID/ContractPartyModal.tsx';
import PartyFullInfoModal from '@/modules/admin-dashboard/components/contractManagement/PartyFullInfoModal.tsx';
import { useDeleteContractPartyMutation } from '@/modules/admin-dashboard/queries/contract-management.ts';
import { Button, EmptyListState, ModalProviderContext, SearchField } from 'ui';
import { PlusIcon, TrashGrayIcon } from 'ui/icons';
import { fixNameEnum } from 'ui/utils';

interface ContractByIDPartiesTabProps {
  contractId: number;
  parties: ContractDetailPartyResponse[];
  readOnly: boolean;
}

const countryDisplayNames =
  typeof Intl !== 'undefined' && 'DisplayNames' in Intl
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

const formatCountry = (country?: string) => {
  if (!country) return null;
  if (country.length !== 2) return country;
  return countryDisplayNames?.of(country.toUpperCase()) || country;
};

export const ContractByIDPartiesTab = ({
  contractId,
  parties,
  readOnly,
}: ContractByIDPartiesTabProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const [searchValue, setSearchValue] = useState('');
  const { mutateAsync: deleteContractParty, isLoading: isDeleting } =
    useDeleteContractPartyMutation();

  const filteredParties = useMemo(() => {
    const term = searchValue.trim().toLowerCase();
    if (!term) return parties;

    return parties.filter(party => {
      const haystack = [
        party.legal_name,
        party.role_type,
        party.incorporation_country,
        party.entity_type,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(term);
    });
  }, [parties, searchValue]);

  const handleAddParty = () => {
    if (readOnly) return;
    openModal(ContractPartyModal, { contractId });
  };

  const handleOpenParty = (partyId: number) => {
    openModal(PartyFullInfoModal, { partyId, readOnly });
  };

  const handleDeleteParty = async (contractPartyId: number) => {
    await deleteContractParty({ id: contractPartyId, contractId });
  };

  return (
    <div className="flex flex-col gap-3">
      {!!parties.length && (
        <div className="flex items-center gap-2">
          <SearchField
            placeholder="Search"
            value={searchValue}
            onChange={setSearchValue}
            allowClear
            className="w-full"
          />
          <div className="h-10 w-10 min-h-10 min-w-10">
            <Button
              type="button"
              className="h-10 w-10 min-w-0 px-0"
              onClick={handleAddParty}
              aria-label="Add party"
              disabled={readOnly}
            >
              <img src={PlusIcon} alt="" className="h-5 w-5 invert" />
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {filteredParties.map(party => (
          <div
            key={party.contract_party_id}
            className={classNames(
              'rounded border border-gray-200 bg-white px-4 py-3 transition-colors',
              'hover:bg-gray-50 hover:cursor-pointer',
              'flex items-start justify-between gap-3',
            )}
            role="button"
            tabIndex={0}
            onClick={() => handleOpenParty(party.party_id)}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleOpenParty(party.party_id);
              }
            }}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span
                  className={classNames(
                    'inline-flex rounded px-2 py-[2px] text-xs leading-5',
                    party.is_active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700',
                  )}
                >
                  {party.is_active ? 'Active' : 'Inactive'}
                </span>
                <span className="text-xs text-violet-500">{fixNameEnum(party.role_type)}</span>
                <CreatedByAiLabel created_with_ai={party.is_ai_captured} />
                {party.is_ai_captured && party.verification_status && (
                  <Badge
                    type={
                      party.verification_status === PartyVerificationStatus.Verified
                        ? 'success'
                        : 'warning'
                    }
                    className="!h-5 !px-1.5 !text-xs"
                  >
                    {fixNameEnum(party.verification_status)}
                  </Badge>
                )}
              </div>
              <div className="mt-2 text-base font-medium text-gray-900">{party.legal_name}</div>
              <div className="mt-1 text-sm text-gray-500">
                {[
                  formatCountry(party.incorporation_country),
                  party.entity_type ? fixNameEnum(party.entity_type) : null,
                ]
                  .filter(Boolean)
                  .join(' • ') || '-'}
              </div>
            </div>

            <button
              type="button"
              className="mt-0.5 flex h-8 w-8 items-center justify-center rounded hover:bg-gray-100 disabled:opacity-50"
              onClick={event => {
                event.stopPropagation();
                if (readOnly || isDeleting) return;
                void handleDeleteParty(party.contract_party_id);
              }}
              disabled={isDeleting || readOnly}
              aria-label="Delete contract party"
            >
              <img src={TrashGrayIcon} alt="" className="h-5 w-5" />
            </button>
          </div>
        ))}

        <EmptyListState
          title="No parties linked to this contract"
          description="Add parties to this contract to track their roles and details."
          isLoading={false}
          isSearchUsed={!!searchValue}
          itemsCount={filteredParties.length}
          classes={{
            container: 'min-h-[500px]',
            title: 'text-xl text-[28px]',
            image: 'h-20 w-20',
            description: 'text-sm',
          }}
          action={
            readOnly
              ? undefined
              : {
                  content: 'Add Party',
                  onClick: handleAddParty,
                }
          }
        />
      </div>
    </div>
  );
};
